import { FC, useId } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'isomorphic-dompurify';

export interface AmplienceTextProps {
  content?: { content: string };
  paddingX?: number;
  customStyles?: { content: string };
}

const AmplienceText: FC<AmplienceTextProps> = ({ content, paddingX = 0, customStyles }) => {
  // Create a unique ID for this instance to scope the CSS
  const stableId = useId();
  const componentId = `amplience-text-${stableId.replace(/:/g, '')}`;

  if (!content) return null;

  // This function processes markdown within HTML tags
  // Content inside HTML tags is ususally not processed, so we need to process it manually
  // It also sanitizes the content with the help of DOMPurify
  const processContent = (content: string) => {
    if (!content) return '';
    // We use regex to find HTML tags with content
    const processedContent = content.replace(/<([a-z][a-z0-9]*)\b[^>]*>(.*?)<\/\1>/gi, (match, tag, innerContent) => {
      const openTagMatch = match.match(/<[^>]*>/);
      if (!openTagMatch) return match;

      const openTag = openTagMatch[0];
      const closeTag = `</${tag}>`;

      const processedInnerContent = innerContent
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
        .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic
        .replace(/~~(.*?)~~/g, '<del>$1</del>') // Strikethrough
        .replace(
          /\[([^\]]+)\]\(\s*([^\s\)]+)(?:\s+["'](.*?)["'])?\s*\)/g,
          (_match: string, text: string, href: string, title?: string): string => {
            const titleAttr = title ? ` title="${title}"` : '';
            return `<a href="${href}"${titleAttr}>${text}</a>`;
          },
        );

      return `${openTag}${processedInnerContent}${closeTag}`;
    });
    const sanitizedContent = DOMPurify.sanitize(processedContent);
    return sanitizedContent;
  };

  // This function sanitizes the CSS with the help of DOMPurify
  // and it scopes the CSS to this component instance only
  const sanitizeCSS = (css: string): string => {
    if (!css) return '';

    const wrapped = `<style>${css}</style>`;
    const config = {
      ALLOWED_TAGS: ['style'],
      ADD_TAGS: ['style'],
      WHOLE_DOCUMENT: true,
      RETURN_DOM: true,
    };

    const sanitizedNode = DOMPurify.sanitize(wrapped, config) as unknown as Document;

    const styleTag = sanitizedNode.querySelector('style');
    if (!styleTag || !styleTag.textContent) return '';

    // Scope the CSS to this component instance
    return styleTag.textContent.replace(
      /(^|\})\s*([^{]+)(\{)/g,
      (match: string, g1: string, g2: string, g3: string) => {
        const selectors = g2
          .split(',')
          .map((s) => `.${componentId} ${s.trim()}`)
          .join(', ');
        return `${g1} ${selectors} ${g3}`;
      },
    );
  };

  return (
    <div className={componentId} style={{ paddingInline: `${paddingX}px` }}>
      <style>
        {`
          .gold {
            color: rgb(var(--color-primary-gold));
          }
          .tag {
            background-color: #E5E7EB;
            padding: 2px 4px;
            font-weight: 500;
            border-radius: 2px;
          }
          ${sanitizeCSS(customStyles?.content || '')}
          .semibold {
            font-weight: 600;
          }
          .lg-text {
            font-size: 20px;
          }
          .lg-text-gold {
            font-size: 20px;
            font-weight: 500;
            color: rgb(var(--color-primary-gold));
          }
          .lg-text-medium {
            font-size: 20px;
            font-weight: 500; 
          }
          .title-4xl-gold {
            font-size: 72px ;
            color: rgb(var(--color-primary-gold));
          }
        `}
      </style>
      <ReactMarkdown
        rehypePlugins={[rehypeRaw]}
        remarkPlugins={[remarkGfm]}
        components={{
          // Custom renderers for different elements
          h1: ({ node, ...props }) => <h1 className="pb-8 pt-14 text-3xl font-semibold" {...props} />,
          h2: ({ node, ...props }) => <h2 className="pb-4 pt-6 text-2xl font-semibold" {...props} />,
          h3: ({ node, ...props }) => <h3 className="pb-2 pt-4 text-lg font-semibold" {...props} />,
          h4: ({ node, ...props }) => <h4 className="pb-2 pt-4 text-base font-semibold" {...props} />,
          h5: ({ node, ...props }) => <h5 className="pb-2 pt-4 text-base font-normal" {...props} />,
          h6: ({ node, ...props }) => <h6 className="pb-2 pt-4 text-sm font-normal" {...props} />,
          a: ({ node, ...props }) => <a className="underline" {...props} />,
          ul: ({ node, ...props }) => <ul className="my-2 list-disc pl-5" {...props} />,
          ol: ({ node, ...props }) => <ol className="my-2 list-decimal pl-5" {...props} />,
          li: ({ node, ...props }) => <li className="my-7" {...props} />,
          p: ({ node, ...props }) => <p className="my-10 whitespace-pre-wrap" {...props} />,
          table: ({ node, ...props }) => <table className="my-4 border-collapse" {...props} />,
          thead: ({ node, ...props }) => <thead {...props} />,
          tbody: ({ node, ...props }) => <tbody {...props} />,
          tr: ({ node, ...props }) => <tr className="border-b border-gray-600" {...props} />,
          th: ({ node, ...props }) => (
            <th className="border border-gray-600 px-5 py-2 text-left font-semibold" {...props} />
          ),
          td: ({ node, ...props }) => <td className="border border-gray-600 px-5 py-2" {...props} />,
        }}
      >
        {processContent(content.content || '')}
      </ReactMarkdown>
    </div>
  );
};

export default AmplienceText;
